import { BillboardCollection, Cartesian3, Cartographic, createGuid, defined, PrimitiveCollection, Scene } from "cesium";

import { MapToolConstructorOptions, MouseEvent, PolygonDrawingHandler, PrimitiveSettings } from "../../../core";
import { EditablePolygon } from "../EditablePolygon";
import { newCartesian3ArrayFromArray } from "../util";

import { PolygonDrawing } from "./PolygonDrawing";
import { DrawingTool, PolygonDrawingData } from "./DrawingTool";
import { Drawing } from "./Drawing";

declare type ConstructorOptions = MapToolConstructorOptions & {
    scene: Scene;
    primitives: PrimitiveCollection;
};

export class PolygonDrawingTool extends DrawingTool {
    private readonly _polygonDrawingHandler: PolygonDrawingHandler;
    private readonly _billboardCollection: BillboardCollection;

    constructor(options: ConstructorOptions) {
        super(options);

        this._billboardCollection = new BillboardCollection();
        this.scene.primitives.add(this._billboardCollection);

        this._polygonDrawingHandler = new PolygonDrawingHandler({
            scene: options.scene,
            primitives: options.primitives,
            pointOptions: PrimitiveSettings.getPointOptions(),
            polylineOptions: PrimitiveSettings.getPolylineOptions({
                allowPicking: false,
                depthTest: false
            }),
            polygonOptions: PrimitiveSettings.getPolygonOptions({
                color: PrimitiveSettings.color.withAlpha(0.8),
                allowPicking: false,
                depthTest: false
            }),
            showVertices: false
        });
    }

    activate(): void {
        super.activate();

        this._polygonDrawingHandler.reset();
    }

    deactivate(): void {
        super.deactivate();

        this._polygonDrawingHandler.reset();
    }

    canvasClickEvent(event: MouseEvent) {
        const position = this._polygonDrawingHandler.handleClick(event.pos);

        if (position && this._polygonDrawingHandler.positions.length === 1) {
            this._drawingStarted.raiseEvent();
        }
    }

    canvasMoveEvent(event: MouseEvent): void {
        if (event.button !== undefined) {
            return;
        }

        const nextPos = this._polygonDrawingHandler.handleMouseMove(event.pos);

        if (!nextPos) {
            return;
        }

        const positions = this._polygonDrawingHandler.positions.slice();

        positions.push(nextPos);

        super.canvasMoveEvent(event);
    }

    canvasDoubleClickEvent() {
        this.finishDrawing();
    }

    finishDrawing(): void {
        const drawingHandler = this._polygonDrawingHandler;

        drawingHandler.handleDoubleClick();

        const polyline = drawingHandler.polyline;
        const polygon = drawingHandler.polygon;

        polyline.allowPicking = true;
        polygon.allowPicking = true;

        this._drawings.push(
            new PolygonDrawing({
                id: createGuid(),
                polygon: new EditablePolygon({
                    billboardCollection: this._billboardCollection,
                    pointPrimitiveCollection: drawingHandler.pointCollection,
                    pointOptions: drawingHandler.pointOptions,
                    points: drawingHandler.points,
                    polyline: polyline,
                    polygon: polygon,
                    showVertices: drawingHandler.showVertices
                })
            })
        );

        this._polygonDrawingHandler.prepareNewPrimitives();

        // @ts-ignore
        this._drawingFinished.raiseEvent(this._drawings.length - 1);
    }

    loadData(data: PolygonDrawingData, id: string) {
        const positions = newCartesian3ArrayFromArray(data.positions);

        const drawingHandler = this._polygonDrawingHandler;

        positions.forEach((position) => {
            drawingHandler.addPoint(position);
        });

        const polyline = drawingHandler.polyline;
        const polygon = drawingHandler.polygon;

        polyline.allowPicking = true;
        polyline.clampToGround = data.clampToGround;

        polygon.allowPicking = true;
        polygon.color = data.color;
        polygon.clampToGround = data.clampToGround;

        const polygonDrawing = new PolygonDrawing({
            id: id,
            polygon: new EditablePolygon({
                billboardCollection: this._billboardCollection,
                pointPrimitiveCollection: drawingHandler.pointCollection,
                pointOptions: drawingHandler.pointOptions,
                points: drawingHandler.points,
                polyline: polyline,
                polygon: polygon,
                showVertices: drawingHandler.showVertices
            })
        });

        this._drawings.push(polygonDrawing);

        drawingHandler.prepareNewPrimitives();
    }

    removeDrawingById(id: string) {
        const drawingResults = this._drawings;

        const index = drawingResults.findIndex((drawingResult) => drawingResult.id === id);

        if (index === -1) {
            return false;
        }

        const foundDrawing = drawingResults[index] as PolygonDrawing;

        const drawing = this._polygonDrawingHandler;

        const points = foundDrawing.pointPrimitives;

        for (let i = 0; i < points.length; i++) {
            drawing.pointCollection.remove(points[i]);
        }

        drawing.primitives.remove(foundDrawing.polylinePrimitive);
        drawing.primitives.remove(foundDrawing.polygon.polygonPrimitive);

        drawingResults.splice(index, 1);

        return true;
    }

    updateDrawing(drawing: Drawing) {
        const polygonDrawing = drawing as PolygonDrawing;

        const polygon = polygonDrawing.polygon;

        if (polygon.editing) {
            return;
        }

        const positions = polygon.polygonPrimitive.positions;

        const updatedPositions: Cartesian3[] = [];

        const scene = this.scene;

        positions.forEach((pos) => {
            const carto = Cartographic.fromCartesian(pos);

            const h = scene.sampleHeight(carto);

            if (!defined(h)) {
                updatedPositions.push(Cartesian3.fromRadians(carto.longitude, carto.latitude, carto.height));

                return;
            }

            // Mariana Trench
            if (h < -10935) {
                updatedPositions.push(Cartesian3.fromRadians(carto.longitude, carto.latitude, carto.height));

                return;
            }

            updatedPositions.push(Cartesian3.fromRadians(carto.longitude, carto.latitude, h));
        });

        polygon.updatePositions(updatedPositions);
    }

    updateDrawings() {
        this._drawings.forEach((drawing) => {
            this.updateDrawing(drawing);
        });
    }
}
